import { ref, type Ref } from 'vue';

import { type DeepiToastProps } from '@deepidoo/deesplay/components/toast/DeepiToast.vue';
import { createSingleton } from '@deepidoo/deesplay/utils';

export enum ToastType {
	SUCCESS = 'SUCCESS',
	WARNING = 'WARNING',
	ERROR = 'ERROR',
};
interface Toast extends DeepiToastProps {
	id: number;
	type?: ToastType;
};

export const useToast = createSingleton((): {
	currentToast: Ref<Toast | null>;
	showToast: (options: Omit<Toast, 'id' | 'modelValue'>) => void;
	closeToast: () => void;
} => {
	const currentToast = ref<Toast | null>(null);
	const queue = ref<Toast[]>([]);
	const nextId = ref(1);

	const showToast = (options: Omit<Toast, 'id' | 'modelValue'>): void => {
		const isDuplicate =
			(currentToast.value &&
				(currentToast.value.type === options.type ||
					(currentToast.value.title === options.title &&
						currentToast.value.text === options.text))) ||
			queue.value.some(
				(toast) =>
					toast.type === options.type ||
					(toast.title === options.title && toast.text === options.text)
			);

		if (isDuplicate) {
			return;
		}

		const toast: Toast = {
			...options,
			id: nextId.value++,
			modelValue: true
		};

		queue.value.push(toast);

		if (!currentToast.value) {
			displayNextToast();
		}
	};

  	const displayNextToast = (): void => {
		if (queue.value.length === 0) {
			currentToast.value = null;
			return;
		}

		currentToast.value = queue.value.shift() ?? null;

		const timeout = currentToast.value?.timeout ?? 5000;
		setTimeout(() => closeToast(), timeout);
  	};

	const closeToast = (): void => {
		currentToast.value = null;
		if (queue.value.length > 0) {
			setTimeout(() => displayNextToast(), 500);
		}
	};

	return {
		currentToast,
		showToast,
		closeToast
	};
});